import React, {useEffect, useRef, useState} from "react";
import {post, page, primaryBtn, darkBtn, Portrait, TimeBar} from "./ui";
import {ZedSays} from "./zed";
import {BumpDemo} from "./bump_demo";

// Kickoff's handshake: tap READY, then fist bump the person next to you with your
// phone in your fist. Each phone posts its impact time on the server's clock; the
// server pairs two bumps that land together. If a bump won't register, tap the
// person you're with instead.
const THRESHOLD = 8;      // m/s², gravity removed; fist bumps measured ~12–24, shaking ~6
// An impact is a near-instant jolt; a reach toward someone's fist builds up smoothly. Counting only a sudden
// jump between two readings saves the trigger for the knuckles (lab, 10/22: bumps jumped ≥4.9 in 90% of
// cases, median 11.8; shaking and waving median 2.3).
const JUMP = 5;
// ...then calm within CALM_MS, watched over SETTLE_MS (lab: 83% of bumps pass, 6% of shakes).
const CALM_MS = 120;
const SETTLE_MS = 200;
const ARM_MS = 5000;
const REFRACTORY_MS = 600;

const nowMs = () => performance.timeOrigin + performance.now();

// The server's clock minus this device's: keep the fastest of 8 round trips.
const syncClock = async () => {
    let best = null;
    for (let i = 0; i < 8; i++) {
        try {
            const sent = nowMs();
            const {time} = await (await fetch('/time', {cache: 'no-store'})).json();
            const rtt = nowMs() - sent;
            if (!best || rtt < best.rtt) best = {rtt, offset: time + rtt / 2 - nowMs()};
        } catch (e) {}
    }
    return best ? {offset: best.offset, bound: best.rtt / 2} : {offset: 0, bound: null};
};

let audio = null;
const ding = () => {
    if (navigator.vibrate) navigator.vibrate(60);
    if (!audio) return;
    const o = audio.createOscillator(), g = audio.createGain();
    o.type = 'triangle'; o.frequency.value = 1760;
    g.gain.setValueAtTime(0.4, audio.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.6);
    o.connect(g).connect(audio.destination); o.start(); o.stop(audio.currentTime + 0.6);
};

export const BumpPhase = ({state, offset}) => {
    const partner = state.you.partner;
    const wasPaired = useRef(!!partner);
    useEffect(() => {
        if (partner && !wasPaired.current) ding();
        wasPaired.current = !!partner;
    }, [partner]);

    if (partner) return <Paired partner={partner} state={state} offset={offset} />;
    return <Bumper state={state} offset={offset} />;
};

const Bumper = ({state, offset}) => {
    const [armedUntil, setArmedUntil] = useState(0);
    const [err, setErr] = useState('');
    const [misses, setMisses] = useState(0); // bump windows that closed without a pair
    const [, tick] = useState(0);
    const clockOffset = useRef(null);
    const armedRef = useRef(0);
    const listening = useRef(false);
    const gravity = useRef(null);
    const lastBump = useRef(0);
    const lastMag = useRef(0);
    const candidate = useRef(null); // a spike being watched to see if it calms down
    const syncBound = useRef(null);

    useEffect(() => { armedRef.current = armedUntil; }, [armedUntil]);
    useEffect(() => {
        const id = setInterval(() => tick(n => n + 1), 250);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const onMotion = (e) => {
            const t = e.timeStamp > 1e12 ? e.timeStamp : performance.timeOrigin + e.timeStamp;
            let a = e.acceleration;
            if (!a || a.x === null) {
                const g = e.accelerationIncludingGravity;
                const gv = gravity.current;
                gravity.current = gv ? {x: gv.x * 0.9 + g.x * 0.1, y: gv.y * 0.9 + g.y * 0.1, z: gv.z * 0.9 + g.z * 0.1} : {...g};
                a = {x: g.x - gravity.current.x, y: g.y - gravity.current.y, z: g.z - gravity.current.z};
            }
            const mag = Math.hypot(a.x, a.y, a.z);
            const jump = mag - lastMag.current;
            lastMag.current = mag;

            // A candidate impact is watched for SETTLE_MS: a bump calms down within CALM_MS, a shake keeps going.
            const c = candidate.current;
            if (c) {
                c.samples.push([t, mag]);
                if (t - c.t < SETTLE_MS) return;
                candidate.current = null;
                const peak = Math.max(...c.samples.map(([, m]) => m));
                const busy = c.samples.filter(([, m]) => m >= peak * 0.5).map(([ts]) => ts);
                if (Math.max(...busy) - Math.min(...busy) > CALM_MS) return; // kept moving
                lastBump.current = c.t;
                post('/demo/bump', {t: c.t + (clockOffset.current || 0), bound: syncBound.current, mag: peak, jump: c.jump}).catch(() => {});
                return;
            }
            if (mag < THRESHOLD || jump < JUMP || t > armedRef.current || t - lastBump.current < REFRACTORY_MS) return;
            candidate.current = {t, jump, samples: [[t, mag]]};
        };
        window.addEventListener('devicemotion', onMotion);
        return () => window.removeEventListener('devicemotion', onMotion);
    }, []);

    const ready = async () => {
        setErr('');
        try {
            if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {}
        if (!listening.current && typeof DeviceMotionEvent !== 'undefined' && DeviceMotionEvent.requestPermission) {
            try {
                const p = await DeviceMotionEvent.requestPermission();
                if (p !== 'granted') { setErr('Motion access was denied. Tap the person you\'re with below.'); return; }
            } catch (e) { setErr('Could not turn on motion. Tap the person you\'re with below.'); return; }
        }
        listening.current = true;
        if (clockOffset.current === null) {
            const sync = await syncClock();
            clockOffset.current = sync.offset;
            syncBound.current = sync.bound;
        }
        setArmedUntil(nowMs() + ARM_MS);
        // Still here when the window closes = no pair (pairing replaces this screen).
        setTimeout(() => setMisses(n => n + 1), ARM_MS);
    };

    const left = Math.ceil((armedUntil - nowMs()) / 1000);
    const armed = left > 0;
    const others = state.players.filter(p => p.token !== state.you.token && !p.paired);

    return <div style={{...page, gap: 18}}>
        <div>
            <ZedSays line={state.players.length === 2
                ? `Tap ready. Then fist bump ${state.players.find(p => p.token !== state.you.token).name}.`
                : 'Find someone. Tap ready. Bump fists.'} />
        </div>
        <BumpDemo />
        <button style={armed ? {...primaryBtn, padding: 28, fontSize: 28, background: 'linear-gradient(#ff8a4c, #e0441c)', color: '#2a0d00',
                                 boxShadow: '0 0 30px rgba(255,74,28,0.55)'} : {...primaryBtn, padding: 28, fontSize: 28}} onClick={ready}>
            {armed ? `BUMP! ${left}s` : 'READY'}
        </button>
        {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
        {/* The tap-a-face fallback only after three missed bump windows (or if motion is off), so nobody skips the bump. */}
        {others.length > 0 && (misses >= 3 || err) && <div style={{marginTop: 'auto'}}>
            <div style={{textAlign: 'center', opacity: 0.6, marginBottom: 8}}>Bump not working? Tap who you're with:</div>
            {others.map(p => (
                <button key={p.token} style={{...darkBtn, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 22}}
                        onClick={() => post('/demo/pair', {token: p.token})}>
                    <Portrait player={p} size={48} /> {p.name}
                </button>
            ))}
        </div>}
    </div>;
};

// After the bump, like the portrait reveal: Zed on top with his line about the two of you,
// your partner's name and nickname, their portrait full-bleed at the bottom.
const Paired = ({partner, state, offset}) => {
    const line = state.you.pair_line || ''; // he stays quiet the second or two until his line is written
    return <div style={{...page, gap: 18, height: '100dvh', minHeight: 0, overflow: 'hidden', position: 'relative', isolation: 'isolate'}}>
        <div data-fly="fade" style={{position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: -1,
            background: 'radial-gradient(ellipse at top, rgba(54,255,54,0.16) 0%, transparent 60%)'}} />
        <TimeBar endsAt={state.phase_ends_at} offset={offset} total={15000} calm />
        <ZedSays line={line} />
        <div style={{textAlign: 'center', marginTop: 4}}>
            <div className="bumped-pop" style={{fontWeight: 900, fontStyle: 'italic', fontSize: 30, letterSpacing: 4, color: '#4dff7a',
                textShadow: '0 0 10px rgba(77,255,122,0.55)', marginBottom: 6}}>BUMPED!</div>
            <div style={{fontSize: 56, fontWeight: 800, lineHeight: 1.0}}>{partner.name}</div>
            <div style={{fontSize: 32, fontStyle: 'italic', color: '#d9b77a', minHeight: 40, marginTop: 6, lineHeight: 1.1}}>{partner.nickname}</div>
        </div>
        <div style={{flex: '1 1 0', minHeight: 0, position: 'relative', overflow: 'hidden', margin: '0 -18px calc(-24px - env(safe-area-inset-bottom))'}}>
            {partner.portrait_url && <img src={partner.portrait_url} alt="" style={{position: 'absolute', top: 0, left: 0, width: '100%', display: 'block'}} />}
        </div>
    </div>;
};

